/**
 * Notification API client (P2-S7).
 * Mirror libs/notifications + apps/auth/api/admin/notifications.py.
 */
import { api } from "./client";

// ============================================================================
// Types
// ============================================================================
export type ChannelType =
  | "email"
  | "zalo_zns"
  | "zalo_oa"
  | "ms_teams"
  | "slack"
  | "sms"
  | "in_app";

export const ALL_CHANNELS: ChannelType[] = [
  "email",
  "zalo_zns",
  "zalo_oa",
  "ms_teams",
  "slack",
  "sms",
  "in_app",
];

export const CHANNEL_LABELS: Record<ChannelType, string> = {
  email: "Email",
  zalo_zns: "Zalo ZNS",
  zalo_oa: "Zalo OA",
  ms_teams: "MS Teams",
  slack: "Slack",
  sms: "SMS",
  in_app: "In-app",
};

export interface ChannelOut {
  id: string;
  channel_type: ChannelType;
  name: string;
  enabled: boolean;
  config_keys: string[];
  created_at: string;
  updated_at: string;
}

export interface ChannelUpsertBody {
  channel_type: ChannelType;
  name: string;
  enabled?: boolean;
  config: Record<string, unknown>;
}

export interface TemplateOut {
  id: string;
  code: string;
  channel_type: ChannelType;
  subject: string | null;
  body: string;
  metadata_json: Record<string, unknown>;
  version: number;
  created_at: string;
}

export interface TemplateUpsertBody {
  code: string;
  channel_type: ChannelType;
  subject?: string | null;
  body: string;
  metadata_json?: Record<string, unknown>;
}

export interface RuleOut {
  id: string;
  name: string;
  event_type: string;
  template_code: string;
  channels: ChannelType[];
  recipients_rule: Record<string, unknown>;
  enabled: boolean;
  quiet_hours: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface RuleUpsertBody {
  name: string;
  event_type: string;
  template_code: string;
  channels: ChannelType[];
  recipients_rule: Record<string, unknown>;
  enabled?: boolean;
  quiet_hours?: Record<string, unknown> | null;
}

export interface TestSendBody {
  channel_type: ChannelType;
  template_code: string;
  recipient: {
    user_id?: string;
    email?: string;
    phone?: string;
    name?: string;
    extra?: Record<string, unknown>;
  };
  context?: Record<string, unknown>;
}

export interface TestSendResponse {
  ok: boolean;
  provider_id: string | null;
  error: string | null;
  rendered: { body: string; subject?: string };
}

export interface TestEventResponse {
  enqueued: number;
  sent: number;
  failed: number;
  skipped: string[];
  notification_ids: string[];
}

// ============================================================================
// User-facing
// ============================================================================
export interface PrefsOut {
  user_id: string;
  email_enabled: boolean;
  zalo_enabled: boolean;
  teams_enabled: boolean;
  slack_enabled: boolean;
  sms_enabled: boolean;
  in_app_enabled: boolean;
  quiet_hours: Record<string, unknown> | null;
  do_not_disturb_until: string | null;
  updated_at: string;
}

export interface PrefsUpdate {
  email_enabled?: boolean;
  zalo_enabled?: boolean;
  teams_enabled?: boolean;
  slack_enabled?: boolean;
  sms_enabled?: boolean;
  in_app_enabled?: boolean;
  quiet_hours?: Record<string, unknown> | null;
  do_not_disturb_until?: string | null;
}

export interface NotificationOut {
  id: string;
  channel: string;
  template_code: string | null;
  event_type: string | null;
  rendered: { body?: string; subject?: string } | null;
  payload: Record<string, unknown>;
  status: string;
  created_at: string;
  sent_at: string | null;
  read_at: string | null;
}

// ============================================================================
// Client
// ============================================================================
export const notificationsApi = {
  // Admin
  listChannels: () =>
    api.get<ChannelOut[]>("/api/v1/admin/notifications/channels"),
  createChannel: (body: ChannelUpsertBody) =>
    api.post<ChannelOut>("/api/v1/admin/notifications/channels", body),
  updateChannel: (id: string, body: ChannelUpsertBody) =>
    api.put<ChannelOut>(`/api/v1/admin/notifications/channels/${id}`, body),
  deleteChannel: (id: string) =>
    api.delete<void>(`/api/v1/admin/notifications/channels/${id}`),

  listTemplates: () =>
    api.get<TemplateOut[]>("/api/v1/admin/notifications/templates"),
  createTemplate: (body: TemplateUpsertBody) =>
    api.post<TemplateOut>("/api/v1/admin/notifications/templates", body),
  deleteTemplate: (id: string) =>
    api.delete<void>(`/api/v1/admin/notifications/templates/${id}`),

  listRules: () => api.get<RuleOut[]>("/api/v1/admin/notifications/rules"),
  createRule: (body: RuleUpsertBody) =>
    api.post<RuleOut>("/api/v1/admin/notifications/rules", body),
  updateRule: (id: string, body: RuleUpsertBody) =>
    api.put<RuleOut>(`/api/v1/admin/notifications/rules/${id}`, body),
  deleteRule: (id: string) =>
    api.delete<void>(`/api/v1/admin/notifications/rules/${id}`),

  testSend: (body: TestSendBody) =>
    api.post<TestSendResponse>("/api/v1/admin/notifications/test-send", body),
  testEvent: (event_type: string, context: Record<string, unknown>) =>
    api.post<TestEventResponse>("/api/v1/admin/notifications/test-event", {
      event_type,
      context,
    }),

  // User
  getPrefs: () => api.get<PrefsOut>("/api/v1/me/notification-prefs"),
  updatePrefs: (body: PrefsUpdate) =>
    api.patch<PrefsOut>("/api/v1/me/notification-prefs", body),

  listMyNotifications: (unread_only = false, limit = 50) =>
    api.get<NotificationOut[]>("/api/v1/me/notifications", {
      query: { unread_only, limit },
    }),
  unreadCount: () =>
    api.get<{ unread: number }>("/api/v1/me/notifications/unread-count"),
  markRead: (id: string) =>
    api.post<{ ok: boolean }>(`/api/v1/me/notifications/${id}/read`),
  markAllRead: () =>
    api.post<{ marked: number }>("/api/v1/me/notifications/read-all"),
};
